import React from 'react';
import './styles.scss';
import Logo from '../../assets/icons/lusidpay_logo.svg';

const ShieldCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const LayoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="9" y1="21" x2="9" y2="9" />
  </svg>
);

const WrenchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const CertifyPage = () => {
  return (
    <div className="certify-page">

      {/* CUSTOM NAV */}
      <nav className="cp-nav">
        <div className="cp-nav-container">
          <span className="cp-nav-brand">LUSID <span className="cp-nav-brand-accent">CERTIFY</span></span>
          <span className="cp-nav-by">by</span>
          <img 
            src={Logo} 
            alt="LusidPay Logo" 
            className="cp-nav-logo" />
        </div>
      </nav>

      {/* HERO */}
      <section className="cp-hero">
        <div className="cp-container">
          <div className="cp-hero-content">
            <div className="cp-compliance-badge">
              <ShieldCheckIcon />
              FHWA &amp; CFR 49 Compliant Architecture
            </div>

            <h1 className="cp-h1">Clear the IIJA Compliance Backlog with Automated DBE Narratives.</h1>

            <p className="cp-hero-desc">
              Architected in strict alignment with Federal Highway Administration (FHWA) evidentiary standards for CFR 49.{' '}
              <strong>Lusid Certify</strong> translates lived experiences into audit-ready defense dossiers in one shot—eliminating RFI loops and accelerating government procurement.
            </p>

            <div className="cp-hero-actions">
              <a
                href="https://scheduler.zoom.us/rajeev-malhotra/30-min"
                target="_blank"
                rel="noopener noreferrer"
                className="cp-btn cp-btn-white"
              >
                Book an Agency Pilot Briefing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DUAL MECHANISM */}
      <section className="cp-section cp-bg-light">
        <div className="cp-container">
          <div className="cp-section-header">
            <p className="cp-section-eyebrow">The Core Mechanism</p>
            <h2 className="cp-h2-large">A Digital Pre-Check for Certification.</h2>
            <p className="cp-section-sub">Lusid Certify operates as a dual-mechanism system, bridging the gap between the applicant's lived experience and the analyst's desk.</p>
          </div>

          <div className="cp-grid-2">
            <div className="cp-mechanism-card cp-card-blue-top">
              <div className="cp-icon-lg">
                <LayoutIcon />
              </div>
              <h3 className="cp-h3">The Intelligent Interview (Front-End)</h3>
              <p className="cp-card-subtitle"><strong>The "TurboTax" for CFR 49 compliance.</strong></p>
              <p>An AI-guided wizard walks the business owner through a secure, plain-English digital interview regarding their social and economic disadvantage. It automatically translates their real-world, lived experience into the required legal and regulatory framework.</p>
            </div>

            <div className="cp-mechanism-card cp-card-navy-top">
              <div className="cp-icon-lg">
                <WrenchIcon />
              </div>
              <h3 className="cp-h3">The Secure Vault &amp; Compiler (Back-End)</h3>
              <p className="cp-card-subtitle"><strong>Zero RFI Loops. One-shot compliance.</strong></p>
              <p>As applicants answer questions, they securely upload supporting evidence (tax returns, bank statements). The system then generates a mathematically mapped, hyperlinked defense dossier ready for immediate analyst review—with no formatting errors or missing evidence.</p>
            </div>
          </div>

          {/* VALUE PROP BOX */}
          <div className="cp-value-prop-box">
            <div className="cp-value-prop-left">
              <span className="cp-eyebrow-bright">For The State DOT / UCP</span>
              <h3 className="cp-value-h3">Clear the IIJA Backlog.</h3>
              <p className="cp-value-p">Eliminate the administrative bottleneck caused by the new USDOT Interim Final Rule. Process individualized narratives rapidly, eliminate endless RFI back-and-forths, and keep your contracting pipelines moving.</p>
            </div>
            <div className="cp-value-prop-divider" />
            <div className="cp-value-prop-right">
              <span className="cp-eyebrow-bright">For The Prime / DBE End User</span>
              <h3 className="cp-value-h3">Bulletproof Application.</h3>
              <p className="cp-value-p">Equip your local businesses with the digital infrastructure to succeed. By providing an intuitive, guided intake process, you eliminate the formatting errors and missing evidence that typically stall reviews—empowering applicants to submit mathematically sound defense dossiers on the first try.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4 STEPS */}
      <section className="cp-section">
        <div className="cp-container">
          <div className="cp-section-header">
            <h2 className="cp-h2">The Path to Audit-Ready Submissions</h2>
            <p className="cp-section-sub">Designed for speed, security, and strict regulatory adherence.</p>
          </div>

          <div className="cp-grid-4">
            <div className="cp-step-card">
              <div className="cp-step-number">1</div>
              <h4 className="cp-h4">AI-Guided Intake</h4>
              <p>The applicant completes a secure, plain-English digital interview meticulously tailored to the latest USDOT evidentiary standards.</p>
            </div>
            <div className="cp-step-card">
              <div className="cp-step-number">2</div>
              <h4 className="cp-h4">Evidence Assembly</h4>
              <p>The system prompts the user to upload necessary financial and historical documentation directly into our SOC-2 compliant, encrypted vault.</p>
            </div>
            <div className="cp-step-card">
              <div className="cp-step-number">3</div>
              <h4 className="cp-h4">Automated CFR 49 Mapping</h4>
              <p>Lusid Certify structures the narrative and hyperlinks the uploaded evidence directly to the corresponding federal regulatory requirements.</p>
            </div>
            <div className="cp-step-card">
              <div className="cp-step-number">4</div>
              <h4 className="cp-h4">Immediate Agency Review</h4>
              <p>The UCP receives a standardized, pre-checked defense dossier. <strong>Zero IT integration required.</strong> There is no change to your workflow—applicants simply upload the output into your existing submission portal.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ENTERPRISE BANNER */}
      <section className="cp-section cp-bg-light cp-section-narrow-top">
        <div className="cp-container">
          <div className="cp-enterprise-banner">
            <div className="cp-enterprise-text">
              <h3 className="cp-enterprise-h3">Agency Deployment &amp; Procurement.</h3>
              <p className="cp-enterprise-p">
                Lusid Certify is available via <strong>Enterprise Pilot Licensing</strong>. Our framework is currently being piloted by leading State DOTs and has been reviewed with no technical objections by federal subject matter experts. We are procurement-ready for your agency.
              </p>
            </div>
            <div>
              <a
                href="https://scheduler.zoom.us/rajeev-malhotra/30-min"
                target="_blank"
                rel="noopener noreferrer"
                className="cp-btn cp-btn-primary cp-btn-lg"
              >
                Book an Agency Pilot Briefing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="cp-footer">
        <div className="cp-footer-inner">
          <div className="cp-footer-brand-row">
            <span className="cp-footer-brand">LUSID <span className="cp-footer-brand-accent">CERTIFY</span></span>
            <span className="cp-footer-by">by</span>
            <img src={Logo} alt="LusidPay" className="cp-footer-logo" />
          </div>
          <p className="cp-footer-copy">&copy; 2026 LusidPay Inc. All Rights Reserved.</p>
          <p className="cp-footer-tagline">Built for secure, audit-ready federal compliance.</p>
        </div>
      </footer>

    </div>
  );
};

export default CertifyPage;